import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { uploadTemplate, uploadChangeList, getAllTemplateManagementData } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';

const TemplateManagement = () => {
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Updated categories - flat structure with all product types at same level
  const categories = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' },
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ];

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading template management data...');
      
      const response = await getAllTemplateManagementData();
      
      if (response.success) {
        setTemplateData(response.data);
        console.log('✓ Template management data loaded successfully');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setUploadError('Failed to load template data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await loadAllData();
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [loadAllData]);

  const handleFileUpload = async (event, productType) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setModalType('error');
      setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      setShowModal(true);
      event.target.value = '';
      return;
    }

    try {
      setUploadingCategory(productType);
      setUploadingType('reference');
      setUploadError('');
      setUploadMessage('');

      const decoded = decodedToken();
      const email = decoded?.email || '';

      if (!email) {
        setModalType('error');
        setModalMessage('Unable to retrieve user email. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      console.log(`Uploading ${file.name} for ${productType} by ${email}...`);

    const response = await uploadTemplate(file, productType, email);
    
    // Check if the response indicates success or failure
    if (response.success) {
      if (response.isDuplicate) {
        setModalType('duplicate');
        setDuplicateInfo(response.existingTemplate);
        setModalMessage(
          `The uploaded template is identical to the existing ${productType} template.\n\n` +
          `No changes detected in:\n` +
          `• Test case mappings\n` +
          `• Test categories\n` +
          `• Change item definitions\n\n` +
          `The existing template will continue to be used.`
        );
        setShowModal(true);
        setUploadMessage(`Template data unchanged. Current version remains active.`);
        setTimeout(() => setUploadMessage(''), 5000);
      } else {
        setModalType('success');
        setModalMessage(`Template uploaded successfully!`);
        setShowModal(true);
        setUploadMessage(`Template uploaded successfully for ${productType}`);
        setTimeout(() => setUploadMessage(''), 5000);
      }
      await loadAllData();
    } else {
      // Handle validation errors returned as success: false
      setModalType('error');
      setModalMessage(response.message || 'Upload failed');
      setShowModal(true);
      setUploadError(response.message || 'Upload failed');
      setTimeout(() => setUploadError(''), 5000);
    }
  } catch (error) {
    console.error('Upload error:', error);
    let errorMessage = 'Unknown error';
    
    // Check if it's a response error with data
    if (error.response?.data) {
      if (error.response.data.success === false) {
        // Backend returned success: false
        errorMessage = error.response.data.message || error.response.data.error;
      } else if (error.response.data.error === 'Invalid template file') {
        errorMessage = error.response.data.message;
        if (error.response.data.availableSheets) {
          errorMessage += `\n\nAvailable sheets in the file:\n${error.response.data.availableSheets.join(', ')}`;
        }
      } else {
        errorMessage = error.response.data.message || error.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setModalType('error');
    setModalMessage(`Failed to upload template:\n\n${errorMessage}`);
    setShowModal(true);
    setUploadError(`Upload failed: ${errorMessage}`);
    setTimeout(() => setUploadError(''), 5000);
  } finally {
    setUploadingCategory(null);
    setUploadingType(null);
    event.target.value = '';
  }
};

const handleChangeListUpload = async (event, productType) => {
  const file = event.target.files[0];
  if (!file) return;

  const validExtensions = ['.xlsx', '.xls', '.xlsm'];
  const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
  
  if (!validExtensions.includes(fileExtension)) {
    setModalType('error');
    setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
    setShowModal(true);
    event.target.value = '';
    return;
  }

  try {
    setUploadingCategory(productType);
    setUploadingType('changelist');
    setUploadError('');
    setUploadMessage('');

    const decoded = decodedToken();
    const email = decoded?.email || '';
    const userType = decoded?.userType || 'PowerAdmin'; // **FIXED: Get userType from JWT**

    if (!email) {
      setModalType('error');
      setModalMessage('Unable to retrieve user email. Please log in again.');
      setShowModal(true);
      event.target.value = '';
      setUploadingCategory(null);
      setUploadingType(null);
      return;
    }

   if (!userType) {
  setModalType('error');
  setModalMessage('Unable to retrieve user type. Please log in again.');
  setShowModal(true);
  event.target.value = '';
  setUploadingCategory(null);
  setUploadingType(null);
  return;
}

      console.log(`Uploading change list ${file.name} for ${productType} by ${email}...`);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('productType', productType);

    // **FIXED: Pass userType to uploadChangeList**
    const response = await uploadChangeList(formData, email, userType);

    if (response.success) {
      if (response.isDuplicate) {
        setModalType('duplicate');
        setDuplicateInfo(response.existingChangeList);
        setModalMessage(
          `The uploaded change list is identical to the existing ${productType} template.\n\n` +
          `No changes detected.\n\n` +
          `The existing template will continue to be used.`
        );
        setShowModal(true);
        setUploadMessage(`Change list unchanged. Current version remains active.`);
        setTimeout(() => setUploadMessage(''), 5000);
      } else {
        setModalType('success');
        setModalMessage(`Change list uploaded successfully!`);
        setShowModal(true);
        setUploadMessage(`Change list uploaded successfully for ${productType}`);
        setTimeout(() => setUploadMessage(''), 5000);
      }
      await loadAllData();
    }
  } catch (error) {
    console.error('Upload error:', error);
    let errorMessage = error.message || 'Unknown error';
    if (error.response?.data?.error === 'Invalid Excel file') {
      errorMessage = error.response.data.message;
      if (error.response.data.expectedSheets) {
        errorMessage += `\n\nExpected sheets: ${error.response.data.expectedSheets.join(', ')}`;
      }
    }
    setModalType('error');
    setModalMessage(`Failed to upload change list:\n\n${errorMessage}`);
    setShowModal(true);
    setUploadError(`Upload failed: ${errorMessage}`);
    setTimeout(() => setUploadError(''), 5000);
  } finally {
    setUploadingCategory(null);
    setUploadingType(null);
    event.target.value = '';
  }
};


  const getDataForCategory = (category) => {
    return templateData.find(item => item.productType === category) || {
      productType: category,
      referenceTable: null,
      changeList: null
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleGoBack = () => {
    navigate('/management');
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setDuplicateInfo(null);
  };

  const getModalStyles = () => {
    switch (modalType) {
      case 'duplicate':
        return {
          bgColor: 'bg-yellow-900/30',
          borderColor: 'border-yellow-500',
          iconColor: 'text-yellow-400',
          icon: Info,
          title: 'Duplicate Template Detected'
        };
      case 'success':
        return {
          bgColor: 'bg-green-900/30',
          borderColor: 'border-green-500',
          iconColor: 'text-green-400',
          icon: CheckCircle,
          title: 'Success'
        };
      case 'error':
      default:
        return {
          bgColor: 'bg-red-900/30',
          borderColor: 'border-red-500',
          iconColor: 'text-red-400',
          icon: AlertCircle,
          title: 'Upload Error'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E1114] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading templates...</p>
        </div>
      </div>
    );
  }

  const modalStyles = getModalStyles();
  const ModalIcon = modalStyles.icon;

  return (
    <div className="min-h-screen bg-[#0E1114] text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-center sm:justify-start relative border-b border-gray-800 pb-4 mb-6">
        <ArrowLeft
          className="w-6 h-6 absolute left-4 sm:static sm:mr-4 cursor-pointer hover:text-blue-400 transition-colors"
          onClick={handleGoBack}
        />
        <h1 className="text-lg font-semibold">Template Management</h1>
      </div>

      {/* Status Messages */}
      {uploadMessage && (
        <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <p className="text-green-300 text-sm">{uploadMessage}</p>
        </div>
      )}

      {uploadError && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300 text-sm">{uploadError}</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${modalStyles.bgColor} border ${modalStyles.borderColor} p-6 rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="flex items-center gap-3 mb-4">
              <ModalIcon className={`w-8 h-8 ${modalStyles.iconColor} flex-shrink-0`} />
              <h3 className="text-lg font-semibold text-white">{modalStyles.title}</h3>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">{modalMessage}</p>
              {duplicateInfo && (
                <div className="mt-4 p-3 bg-gray-800/50 rounded border border-gray-700">
                  <p className="text-xs text-gray-400 mb-1">
                    <span className="font-semibold">Filename:</span> {duplicateInfo.filename}
                  </p>
                  <p className="text-xs text-gray-400">
                    <span className="font-semibold">Last Updated:</span> {formatDate(duplicateInfo.uploadDate)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  modalType === 'duplicate' ? 'bg-yellow-600 hover:bg-yellow-500' :
                  modalType === 'success' ? 'bg-green-600 hover:bg-green-500' :
                  'bg-blue-600 hover:bg-blue-500'
                } text-white active:scale-95`}
                onClick={closeModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Sections */}
      {categories.map((category, index) => {
        const data = getDataForCategory(category.id);
        const template = data.referenceTable;
        const changeList = data.changeList;
        const isUploadingReference = uploadingCategory === category.id && uploadingType === 'reference';
        const isUploadingChangeList = uploadingCategory === category.id && uploadingType === 'changelist';

        return (
          <div key={category.id} className="mt-6 pb-6 border-b border-gray-800 last:border-b-0">
            <h2 className="text-base font-bold mb-4 text-blue-400">{category.label}</h2>

            {/* SECTION 1: HW Change Assessment Reference Table - SIMPLIFIED */}
            <h3 className="text-sm font-medium mb-2 text-cyan-400">HW Change Assessment Reference Table</h3>
            <div className="bg-gray-900/50 rounded-lg p-4 mb-3 border border-gray-800 hover:border-gray-700 transition-colors">
              {template ? (
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white mb-2">{template.filename}</p>
                      
                      {/* SIMPLIFIED: Only show Uploaded by / Edited by and Last updated */}
                      {template.editedBy ? (
                        <p className="text-xs text-blue-300 font-medium">
                          Edited by: <span className="text-blue-200">{template.editedBy}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">
                          Uploaded by: <span className="text-gray-300">{template.uploadedBy}</span>
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-400">
                        Last updated: {formatDate(template.lastModified || template.uploadDate)}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <AlertCircle className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No template uploaded for {category.label}</p>
                </div>
              )}
            </div>

            <div className="w-full flex justify-end mb-6">
              <label className={`bg-gray-700 text-sm text-white px-4 py-2 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600 hover:shadow-lg active:scale-95'
              }`}>
                {isUploadingReference ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {template ? 'Update Template' : 'Upload Template'}
                  </>
                )}
                <input type="file" className="hidden" accept=".xlsx,.xls,.xlsm" onChange={(e) => handleFileUpload(e, category.id)} disabled={isUploadingReference} />
              </label>
            </div>

            {/* SECTION 2: HW Change Item List - SIMPLIFIED */}
            <h3 className="text-sm font-medium mb-2 text-green-400">HW Change Item List</h3>
            <div className="bg-gray-900/50 rounded-lg p-4 mb-3 border border-gray-800 hover:border-gray-700 transition-colors">
              {changeList ? (
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white mb-2">{changeList.filename}</p>
                      
                      {/* SIMPLIFIED: Only show Uploaded by / Edited by and Last updated */}
                      {changeList.editedBy ? (
                        <p className="text-xs text-blue-300 font-medium">
                          Edited by: <span className="text-blue-200">{changeList.editedBy}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">
                          Uploaded by: <span className="text-gray-300">{changeList.uploadedBy}</span>
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-400">
                        Last updated: {formatDate(changeList.uploadDate)}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <AlertCircle className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No change list uploaded for {category.label}</p>
                </div>
              )}
            </div>

            <div className="w-full flex justify-end">
              <label className={`bg-gray-700 text-sm text-white px-4 py-2 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                isUploadingChangeList ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600 hover:shadow-lg active:scale-95'
              }`}>
                {isUploadingChangeList ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {changeList ? 'Update Template' : 'Upload Template'}
                  </>
                )}
                <input type="file" className="hidden"accept=".xlsx,.xls,.xlsm" onChange={(e) => handleChangeListUpload(e, category.id)} disabled={isUploadingChangeList} />
              </label>
            </div>
          </div>
        );
      })}

      {/* Template Upload Guidelines */}
      <div className="mt-8 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-400 mb-2">Template Upload Guidelines</h3>
        <ul className="text-xs text-gray-400 space-y-1">
          <li>• Upload Excel files (.xlsx, .xls, or .xlsm)</li>
          <li>• Each template should contain test case mappings for change items</li>
          <li>• Templates will be used to generate verification test cases automatically</li>
          <li>• Uploading a new template will replace the existing one for that product type</li>
          <li>• Supported product types: TV, MNT, PD (Signage & IWB, RGB LED, AIO, EPD), AVA (Audio, Headphone)</li>
          <li>• Ensure the Excel file contains sheets for EE, PWR, ME, SW, and LCM</li>
        </ul>
      </div>

      {/* Template Status Section - Shows file version */}
      {templateData.length > 0 && (
        <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Template Status</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map(category => {
              const data = getDataForCategory(category.id);
              const hasTemplate = data.referenceTable;
              return (
                <div key={category.id} className="text-center">
                  <div className = "flex items-center justify-center gap-1.5">
                    <div 
                   className={`text-lg ${hasTemplate ? 'text-green-400' : 'text-gray-600'} mt-1`}>
                    {hasTemplate ? '✓' : '○'}
                  </div>
                  <div className = "text-center">
                  <div className="text-xs text-gray-400 font-semibold">{category.label}</div>
                  {hasTemplate && hasTemplate.fileVersion && (
                    <div className="text-xs text-gray-500  font-mono mt-0">
                      {hasTemplate.fileVersion}
                    </div>
                  )}
                </div>
                </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
